/**
 * Orders list page component
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrders, useOrderSync } from '../hooks/useOrders';
import { OrderStatus, toDate, Order } from '../types';
import { SyncProgressBar } from '../components/SyncProgressBar';
import { SilviaLoader } from '../components/SilviaLoader';
import { wywolajFunkcje } from '../services/funkcje';
import '../styles/Orders.css';

interface ProcessingResult {
  orderId: string;
  woocommerceOrderId: string;
  success: boolean;
  supplierOrderId?: string;
  totalAmount?: number;
  errorMessage?: string;
  skipped?: boolean;
  skipReason?: string;
}

/**
 * Order workflow state - represents the 4 main states of an order
 * 1. reservation - wc-processing with payment_status = 'reservation'
 * 2. needs_ordering - wc-processing without supplier_order_id (ready for bot)
 * 3. ordered_at_supplier - wc-processing with supplier_order_id (done, waiting for WC completion)
 * 4. completed - wc-completed (done, closed)
 */
type OrderWorkflowState = 'reservation' | 'needs_ordering' | 'ordered_at_supplier' | 'completed' | 'other';

/**
 * Determines the workflow state of an order based on WC status, payment_status, and supplier_order_id
 */
function getOrderWorkflowState(order: Order): OrderWorkflowState {
  const wcStatus = order.wc_status || '';
  
  // Completed orders (wc-completed or wc-refunded, etc.)
  if (wcStatus === 'completed' || wcStatus === 'wc-completed') {
    return 'completed';
  }
  
  // Check if order is in processing state
  const isProcessing = wcStatus === 'processing' || wcStatus === 'wc-processing' || wcStatus === '';
  
  if (isProcessing) {
    // Reservation - payment_status = 'reservation'
    if (order.payment_status === 'reservation') {
      return 'reservation';
    }
    
    // Has supplier_order_id = already ordered at supplier
    if (order.supplier_order_id) {
      return 'ordered_at_supplier';
    }
    
    // No supplier_order_id = needs to be ordered by bot
    return 'needs_ordering';
  }
  
  // Other states (on-hold, cancelled, refunded, etc.)
  return 'other';
}

/**
 * Get CSS class for workflow state
 */
function getWorkflowStateClass(state: OrderWorkflowState): string {
  switch (state) {
    case 'reservation': return 'workflow-reservation';
    case 'needs_ordering': return 'workflow-needs-ordering';
    case 'ordered_at_supplier': return 'workflow-ordered';
    case 'completed': return 'workflow-completed';
    default: return 'workflow-other';
  }
}

/**
 * Get workflow state badge text
 */
function getWorkflowStateBadge(state: OrderWorkflowState): { text: string; emoji: string } {
  switch (state) {
    case 'reservation': return { text: 'Rezerwacja', emoji: '📅' };
    case 'needs_ordering': return { text: 'Do zamówienia', emoji: '🛒' };
    case 'ordered_at_supplier': return { text: 'Zamówiono', emoji: '✅' };
    case 'completed': return { text: 'Zakończone', emoji: '🏁' };
    default: return { text: 'Inne', emoji: '❓' };
  }
}

export function Orders() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<OrderStatus | undefined>(undefined);
  const { orders: rawOrders, loading } = useOrders(statusFilter, 100);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingResults, setProcessingResults] = useState<ProcessingResult[] | null>(null);
  const { syncOrdersBatch, syncing: isSyncing, progress: syncProgress, resetProgress } = useOrderSync();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  
  // Sorting state - default: pending first, then by date descending
  const [sortColumn, setSortColumn] = useState<'wc_order' | 'workflow' | 'wc_status' | 'bot_status' | 'items' | 'customer' | 'created' | 'supplier_id'>('workflow');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Sort orders
  const orders = [...rawOrders].sort((a, b) => {
    let comparison = 0;
    
    switch (sortColumn) {
      case 'wc_order':
        comparison = parseInt(a.woocommerce_order_id) - parseInt(b.woocommerce_order_id);
        break;
      case 'workflow':
        // Priority: needs_ordering first, then reservation, then ordered, then completed
        const workflowPriority: Record<OrderWorkflowState, number> = {
          needs_ordering: 1,
          reservation: 2,
          ordered_at_supplier: 3,
          completed: 4,
          other: 5,
        };
        comparison = workflowPriority[getOrderWorkflowState(a)] - workflowPriority[getOrderWorkflowState(b)];
        break;
      case 'wc_status':
        comparison = (a.wc_status || '').localeCompare(b.wc_status || '');
        break;
      case 'bot_status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'items':
        comparison = a.items.length - b.items.length;
        break;
      case 'customer':
        comparison = `${a.customer.first_name} ${a.customer.last_name}`.localeCompare(
          `${b.customer.first_name} ${b.customer.last_name}`
        );
        break;
      case 'created':
        comparison = toDate(a.created_at).getTime() - toDate(b.created_at).getTime();
        break;
      case 'supplier_id':
        comparison = (a.supplier_order_id || '').localeCompare(b.supplier_order_id || '');
        break;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Handle column header click
  const handleSort = (column: typeof sortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column: typeof sortColumn) => {
    if (sortColumn !== column) return <span className="sort-indicator">⇅</span>;
    return <span className="sort-indicator active">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Auto-hide toast after 3 seconds
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Navigate to order details - used for row clicks
  const handleRowClick = (orderId: string, event: React.MouseEvent) => {
    // Don't navigate if clicking on checkbox or action buttons
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('a')) {
      return;
    }
    navigate(`/orders/${orderId}`);
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(orderId);
      } else {
        newSet.delete(orderId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select only pending orders (can be processed)
      const pendingOrderIds = orders
        .filter(o => o.status === 'pending' || o.status === 'failed')
        .map(o => o.id);
      setSelectedOrders(new Set(pendingOrderIds));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const canProcessOrder = (order: Order) => {
    return order.status === 'pending' || order.status === 'failed';
  };

  const processSelectedOrders = async () => {
    if (selectedOrders.size === 0) return;
    
    const orderIds = Array.from(selectedOrders);
    
    if (orderIds.length > 5) {
      alert('Możesz przetworzyć maksymalnie 5 zamówień naraz.');
      return;
    }
    
    const confirmMessage = `Czy na pewno chcesz złożyć ${orderIds.length} zamówień u dostawcy?`;
    if (!confirm(confirmMessage)) return;
    
    setIsProcessing(true);
    setProcessingResults(null);
    
    try {
      const response = await wywolajFunkcje('processOrdersManually', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderIds }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setProcessingResults(data.results);
        setSelectedOrders(new Set());
        // Orders will auto-refresh via Firestore real-time listener
      } else {
        alert(`Błąd: ${data.error || 'Nieznany błąd'}`);
      }
    } catch (error) {
      alert(`Błąd połączenia: ${error instanceof Error ? error.message : 'Nieznany błąd'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const closeResults = () => {
    setProcessingResults(null);
  };

  // Sync all pending orders with WooCommerce
  // Only syncs orders that are still potentially in wc-processing status
  const syncAllPendingOrders = async () => {
    // Filter orders that:
    // 1. Are pending status in our system
    // 2. Don't have supplier_order_id yet
    // 3. Don't have wc_sync_status indicating they're no longer in wc-processing
    const skipStatuses = ['not_found_in_wc', 'wc_status_changed', 'wc_trashed'];
    const pendingOrderIds = orders
      .filter(o => 
        o.status === 'pending' && 
        !o.supplier_order_id &&
        (!o.wc_sync_status || !skipStatuses.includes(o.wc_sync_status))
      )
      .map(o => o.id);

    if (pendingOrderIds.length === 0) {
      showToast('Brak zamówień do synchronizacji.');
      return;
    }

    const results = await syncOrdersBatch(pendingOrderIds);
    
    if (results.length > 0) {
      const alreadyProcessed = results.filter(r => r.hasSupplierOrder).length;
      const skipped = results.filter(r => r.skipped).length;
      const synced = results.filter(r => r.synced).length;
      
      let message = `✅ Zsynchronizowano ${synced} zamówień`;
      if (alreadyProcessed > 0) {
        message += ` (${alreadyProcessed} już przetworzonych w WC)`;
      }
      if (skipped > 0) {
        message += ` (${skipped} pominiętych)`;
      }
      showToast(message);
    }
  };

  const exportToCSV = () => {
    if (orders.length === 0) return;

    // Define CSV headers
    const headers = [
      'WooCommerce Order ID',
      'Supplier Order ID',
      'Status',
      'Date',
      'Customer Name',
      'Customer Company',
      'Total Amount',
      'Items Count',
      'Failed Items'
    ];

    // Format rows
    const rows = orders.map((order: Order) => [
      order.woocommerce_order_id,
      order.supplier_order_id || '',
      order.status,
      toDate(order.created_at).toISOString().split('T')[0],
      `${order.customer.first_name} ${order.customer.last_name}`,
      order.customer.company || '',
      order.total_amount ? order.total_amount.toFixed(2).replace('.', ',') : '',
      order.items.length,
      order.failed_items.join(', ')
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="orders-page">
      {/* Sync Progress Bar */}
      <SyncProgressBar 
        progress={syncProgress} 
        onClose={resetProgress}
      />

      {/* Processing Results Modal */}
      {processingResults && (
        <div className="results-modal-overlay" onClick={closeResults}>
          <div className="results-modal" onClick={e => e.stopPropagation()}>
            <h2>Wyniki przetwarzania</h2>
            <div className="results-list">
              {processingResults.map(result => (
                <div key={result.orderId} className={`result-item ${result.success ? 'success' : 'error'}`}>
                  <span className="result-icon">{result.success ? '✅' : '❌'}</span>
                  <div className="result-details">
                    <strong>#{result.woocommerceOrderId}</strong>
                    {result.success ? (
                      <>
                        <span className="supplier-id">{result.supplierOrderId}</span>
                        {result.totalAmount && (
                          <span className="amount">{result.totalAmount.toFixed(2)} zł</span>
                        )}
                      </>
                    ) : (
                      <span className="error-msg">{result.errorMessage}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button className="close-btn" onClick={closeResults}>Zamknij</button>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="toast-notification">
          {toastMessage}
        </div>
      )}

      <div className="page-header">
        <h1>Orders</h1>
        <div className="filters">
          <label>Filter by status:</label>
          <select
            value={statusFilter || 'all'}
            onChange={(e) => setStatusFilter(e.target.value === 'all' ? undefined : e.target.value as OrderStatus)}
          >
            <option value="all">All Active</option>
            <option value="pending">⏳ Pending (do zamówienia)</option>
            <option value="reservation">📅 Rezerwacje</option>
            <option value="processing">🔄 Processing</option>
            <option value="ordered_awaiting_payment">💳 Awaiting Payment</option>
            <option value="ordered_by_woocommerce">📦 Ordered by WC</option>
            <option value="completed">✅ Completed</option>
            <option value="failed">❌ Failed</option>
            <option value="partial">⚠️ Partial</option>
            <option value="product_unavailable">🚫 Product Unavailable</option>
          </select>
          <button 
            className="sync-all-btn" 
            onClick={syncAllPendingOrders}
            disabled={isSyncing || orders.filter(o => o.status === 'pending').length === 0}
            title="Sprawdź wszystkie pending zamówienia w WooCommerce"
          >
            {isSyncing ? '⏳ Synchronizuję...' : '🔄 Sync z WooCommerce'}
          </button>
          <button 
            className="export-button" 
            onClick={exportToCSV}
            disabled={orders.length === 0}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Selected orders action bar */}
      {selectedOrders.size > 0 && (
        <div className="selection-bar">
          <span>{selectedOrders.size} zamówień zaznaczonych</span>
          <button 
            className="process-btn"
            onClick={processSelectedOrders}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="spinner"></span>
                Przetwarzanie...
              </>
            ) : (
              <>🛒 Złóż zamówienia u dostawcy</>
            )}
          </button>
          <button 
            className="clear-selection-btn"
            onClick={() => setSelectedOrders(new Set())}
            disabled={isProcessing}
          >
            Wyczyść zaznaczenie
          </button>
        </div>
      )}

      {loading ? (
        <SilviaLoader text="Ładowanie zamówień..." />
      ) : orders.length > 0 ? (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    checked={selectedOrders.size > 0 && orders.filter(canProcessOrder).every(o => selectedOrders.has(o.id))}
                    disabled={isProcessing}
                  />
                </th>
                <th className="sortable" onClick={() => handleSort('wc_order')}>
                  WC Order ID {renderSortIndicator('wc_order')}
                </th>
                <th className="sortable" onClick={() => handleSort('workflow')}>
                  Workflow {renderSortIndicator('workflow')}
                </th>
                <th className="sortable" onClick={() => handleSort('wc_status')}>
                  WC Status {renderSortIndicator('wc_status')}
                </th>
                <th className="sortable" onClick={() => handleSort('bot_status')}>
                  Bot Status {renderSortIndicator('bot_status')}
                </th>
                <th className="sortable" onClick={() => handleSort('items')}>
                  Items {renderSortIndicator('items')}
                </th>
                <th className="sortable" onClick={() => handleSort('customer')}>
                  Customer {renderSortIndicator('customer')}
                </th>
                <th>Delivery</th>
                <th className="sortable" onClick={() => handleSort('created')}>
                  Created {renderSortIndicator('created')}
                </th>
                <th className="sortable" onClick={() => handleSort('supplier_id')}>
                  Supplier ID {renderSortIndicator('supplier_id')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const workflowState = getOrderWorkflowState(order);
                const workflowBadge = getWorkflowStateBadge(workflowState);
                const workflowClass = getWorkflowStateClass(workflowState);
                
                return (
                <tr 
                  key={order.id} 
                  className={`clickable-row ${workflowClass} ${selectedOrders.has(order.id) ? 'selected' : ''}`}
                  onClick={(e) => handleRowClick(order.id, e)}
                  title="Kliknij, aby zobaczyć szczegóły"
                >
                  <td className="checkbox-col" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedOrders.has(order.id)}
                      onChange={(e) => handleSelectOrder(order.id, e.target.checked)}
                      disabled={!canProcessOrder(order) || isProcessing}
                    />
                  </td>
                  <td>#{order.woocommerce_order_id}</td>
                  <td>
                    <span className={`workflow-badge ${workflowClass}`}>
                      {workflowBadge.emoji} {workflowBadge.text}
                    </span>
                  </td>
                  <td>
                    {order.wc_status ? (
                      <span className={`status-badge wc-status wc-${order.wc_status}${order.wc_meta?.order_complaint ? ' has-complaint' : ''}`}>
                        {order.wc_status}
                      </span>
                    ) : (
                      <span className="status-badge wc-status wc-unknown">-</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge bot-status ${order.status.replace(/_/g, '-')}`}>
                      {order.status === 'ordered_awaiting_payment' ? 'Awaiting Payment' : 
                       order.status === 'ordered_by_woocommerce' ? 'Synced from WC' :
                       order.status === 'product_unavailable' ? 'Unavailable' :
                       order.status}
                    </span>
                  </td>
                  <td>
                    {order.items.length} items
                    {order.failed_items.length > 0 && (
                      <span className="failed-count"> ({order.failed_items.length} failed)</span>
                    )}
                  </td>
                  <td>
                    {order.customer.first_name} {order.customer.last_name}
                    {order.customer.company && (
                      <div className="company">{order.customer.company}</div>
                    )}
                  </td>
                  <td>
                    {order.shipping.delivery_method === 'paczkomat' ? (
                      <>Paczkomat<br />{order.shipping.paczkomat_code}</>
                    ) : (
                      'Kurier'
                    )}
                  </td>
                  <td>{toDate(order.created_at).toLocaleString()}</td>
                  <td>
                    {order.supplier_order_id ? (
                      <span className="supplier-id">{order.supplier_order_id}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="actions-col">
                    <span className="view-hint">👁️</span>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty">
          {statusFilter
            ? `No ${statusFilter} orders found`
            : 'No orders yet'}
        </div>
      )}
    </div>
  );
}
